import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "react-query";
import { io } from "socket.io-client";
import { toast } from "react-toastify";
import * as Icon from "react-feather";
import { HOST } from "../constants";
import { history } from "../history";
import SoporteService from "../service/soporte.service";
import { UserContext } from "./context";
import { getNotificationPresentation } from "./notifications.helpers";
import "../assets/scss/components/notifications.scss";

const MAX_NOTIFICATIONS = 25;
const STORAGE_PREFIX = "notifications:inbox:";
const NOTIFICATION_EVENT = "notifications:new";
const NOTIFICATION_READY_EVENT = "notifications:ready";
const NOTIFICATION_SYNC_INTERVAL_MS = 30000;
const soporteService = new SoporteService();

const notificationIconMap = {
  bell: Icon.Bell,
  clipboard: Icon.Clipboard,
  "check-circle": Icon.CheckCircle,
  "alert-circle": Icon.AlertCircle,
  "alert-triangle": Icon.AlertTriangle,
};

const defaultContextValue = {
  notifications: [],
  unreadCount: 0,
  isConnected: false,
  markAsRead: () => {},
  markAllAsRead: () => {},
  clearNotifications: () => {},
  openNotification: () => {},
};

export const NotificationsContext = createContext(defaultContextValue);

const readSessionUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem("user") || "{}");
  } catch (error) {
    return {};
  }
};

const readSessionToken = () =>
  String(sessionStorage.getItem("token") || "").trim();

const getSessionUserId = (user = {}) => {
  const candidates = [
    user?.verifyLogin?.id,
    user?.iduser,
    user?.data?.user?.verifyLogin?.id,
    user?.data?.verifyLogin?.id,
    user?.id,
  ];

  const numericUserId = candidates
    .map((value) => Number(value))
    .find((value) => Number.isFinite(value) && value > 0);

  return numericUserId || 0;
};

const getSocketBaseUrl = () => {
  const configuredHost = String(HOST || "").trim();
  if (!configuredHost) return window.location.origin;

  return configuredHost
    .replace(/\/api\/v1\/?$/i, "")
    .replace(/\/+$/g, "");
};

const normalizeNotification = (notification = {}) => ({
  id:
    notification?.id ||
    `notification_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  type: String(notification?.type || "general"),
  scope: String(notification?.scope || "general"),
  level: String(notification?.level || "info"),
  title: String(notification?.title || "Nueva notificación"),
  message: String(notification?.message || ""),
  action:
    notification?.action && typeof notification.action === "object"
      ? notification.action
      : null,
  metadata:
    notification?.metadata && typeof notification.metadata === "object"
      ? notification.metadata
      : {},
  queryKeys: Array.isArray(notification?.queryKeys)
    ? notification.queryKeys
    : [],
  createdAt:
    notification?.createdAt ||
    notification?.created_at ||
    new Date().toISOString(),
  readAt: notification?.readAt || notification?.read_at || null,
  read:
    notification?.read === true ||
    Boolean(notification?.readAt) ||
    Boolean(notification?.read_at),
});

const readStoredNotifications = (storageKey) => {
  if (!storageKey) return [];

  try {
    const parsed = JSON.parse(sessionStorage.getItem(storageKey) || "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeNotification).slice(0, MAX_NOTIFICATIONS);
  } catch (error) {
    return [];
  }
};

const NotificationToastBody = ({ notification }) => {
  const presentation = getNotificationPresentation(notification);
  const Glyph =
    notificationIconMap[presentation.iconName] || notificationIconMap.bell;

  return (
    <div className="crm-notification-toast__content">
      <span className="crm-notification-toast__icon">
        <Glyph size={18} />
      </span>

      <span className="crm-notification-toast__text">
        <span className="crm-notification-toast__eyebrow">
          {presentation.badgeLabel}
        </span>
        <span className="crm-notification-toast__title">
          {presentation.title}
        </span>
        {presentation.message ? (
          <span className="crm-notification-toast__message">
            {presentation.message}
          </span>
        ) : null}
        {presentation.metaLine ? (
          <span className="crm-notification-toast__meta">
            {presentation.metaLine}
          </span>
        ) : null}
        <span className="crm-notification-toast__helper">
          {presentation.helperText || presentation.actionLabel}
        </span>
      </span>
    </div>
  );
};

export const NotificationsProvider = ({ children }) => {
  const { isAuth } = useContext(UserContext);
  const queryClient = useQueryClient();
  const socketRef = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [isConnected, setIsConnected] = useState(false);

  const sessionUser = readSessionUser();
  const sessionToken = readSessionToken();
  const currentUserId = useMemo(
    () => getSessionUserId(sessionUser),
    [sessionUser],
  );
  const storageKey = useMemo(
    () => (currentUserId ? `${STORAGE_PREFIX}${currentUserId}` : ""),
    [currentUserId],
  );

  const syncNotifications = useCallback((incomingNotifications = []) => {
    const normalized = incomingNotifications
      .map(normalizeNotification)
      .sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
      .slice(0, MAX_NOTIFICATIONS);

    setNotifications(normalized);
  }, []);

  const loadNotifications = useCallback(async () => {
    if (!isAuth || !currentUserId) return;

    try {
      const response = await soporteService.listNotifications({
        limit: MAX_NOTIFICATIONS,
      });

      if (response?.success) {
        syncNotifications(response.data || []);
      }
    } catch (error) {}
  }, [currentUserId, isAuth, syncNotifications]);

  useEffect(() => {
    if (!storageKey) {
      setNotifications([]);
      return;
    }

    setNotifications(readStoredNotifications(storageKey));
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey) return;
    sessionStorage.setItem(
      storageKey,
      JSON.stringify(notifications.slice(0, MAX_NOTIFICATIONS)),
    );
  }, [notifications, storageKey]);

  const markAsRead = useCallback((notificationId) => {
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === notificationId
          ? {
              ...notification,
              read: true,
              readAt: notification.readAt || new Date().toISOString(),
            }
          : notification,
      ),
    );
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!notifications.length) return;

    setNotifications((prev) =>
      prev.map((notification) => ({
        ...notification,
        read: true,
        readAt: notification.readAt || new Date().toISOString(),
      })),
    );
    await soporteService.markAllNotificationsRead();
  }, [notifications.length]);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const navigateToNotification = useCallback((notification) => {
    if (!notification?.action?.path) return;

    history.push(
      notification.action.path,
      notification.action.state && typeof notification.action.state === "object"
        ? notification.action.state
        : {},
    );
  }, []);

  const openNotification = useCallback(
    async (notification) => {
      if (!notification?.id) return;
      markAsRead(notification.id);
      try {
        await soporteService.markNotificationRead({
          notification_id: notification.id,
        });
      } catch (error) {}
      navigateToNotification(notification);
    },
    [markAsRead, navigateToNotification],
  );

  const pushNotification = useCallback(
    (incomingNotification) => {
      const notification = normalizeNotification(incomingNotification);
      const presentation = getNotificationPresentation(notification);

      setNotifications((prev) =>
        [notification, ...prev.filter((item) => item.id !== notification.id)]
          .slice(0, MAX_NOTIFICATIONS),
      );

      notification.queryKeys
        .filter((queryKey) => Array.isArray(queryKey) && queryKey.length > 0)
        .forEach((queryKey) => {
          queryClient.invalidateQueries(queryKey);
        });

      toast.info(<NotificationToastBody notification={notification} />, {
        toastId: notification.id,
        autoClose: 8000,
        icon: false,
        className: "crm-notification-toast",
        bodyClassName: "crm-notification-toast__body",
        progressClassName: "crm-notification-toast__progress",
        style: {
          "--notification-accent": presentation.accentColor,
          "--notification-soft": presentation.softColor,
        },
        onClick: () => openNotification(notification),
      });
    },
    [openNotification, queryClient],
  );

  useEffect(() => {
    if (!isAuth || !sessionToken || !currentUserId) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setIsConnected(false);
      return undefined;
    }

    loadNotifications();

    const socket = io(getSocketBaseUrl(), {
      auth: {
        token: `Bearer ${sessionToken}`,
      },
      transports: ["polling", "websocket"],
      reconnection: true,
      upgrade: true,
    });

    socketRef.current = socket;

    const handleConnect = () => {
      setIsConnected(true);
      loadNotifications();
    };
    const handleReady = () => {
      setIsConnected(true);
      loadNotifications();
    };
    const handleDisconnect = () => setIsConnected(false);
    const handleConnectError = () => setIsConnected(false);

    socket.on("connect", handleConnect);
    socket.on(NOTIFICATION_READY_EVENT, handleReady);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleConnectError);
    socket.on(NOTIFICATION_EVENT, pushNotification);

    return () => {
      socket.off("connect", handleConnect);
      socket.off(NOTIFICATION_READY_EVENT, handleReady);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleConnectError);
      socket.off(NOTIFICATION_EVENT, pushNotification);
      socket.disconnect();
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
      setIsConnected(false);
    };
  }, [currentUserId, isAuth, loadNotifications, pushNotification, sessionToken]);

  useEffect(() => {
    if (!isAuth || !currentUserId) return undefined;

    const intervalId = window.setInterval(() => {
      loadNotifications();
    }, NOTIFICATION_SYNC_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        loadNotifications();
      }
    };

    window.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [currentUserId, isAuth, loadNotifications]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount: notifications.filter((notification) => !notification.read)
        .length,
      isConnected,
      markAsRead,
      markAllAsRead,
      clearNotifications,
      openNotification,
    }),
    [
      clearNotifications,
      isConnected,
      markAllAsRead,
      markAsRead,
      notifications,
      openNotification,
    ],
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationsContext);
